"use client"

import { useRef, useMemo, useEffect, useState } from "react"
import { Canvas, useFrame, useThree } from "@react-three/fiber"
import { Float, MeshDistortMaterial } from "@react-three/drei"
import * as THREE from "three"

// Custom hook to track scroll position with throttling
function useScrollProgress() {
  const [scrollY, setScrollY] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(1)
  const rafId = useRef<number | null>(null)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      // Throttle using RAF
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(() => {
          lastScrollY.current = window.scrollY
          setScrollY(lastScrollY.current)
          rafId.current = null
        })
      }
    }

    const handleResize = () => {
      setViewportHeight(window.innerHeight)
    }

    handleResize()
    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleResize, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [])

  // Return normalized scroll progress (0 to 1 for first viewport)
  return Math.min(scrollY / viewportHeight, 1)
}

// Single floating geometric shape with distortion
function FloatingShape({
  position,
  rotation,
  scale,
  color,
  speed = 1,
  distort = 0.3,
  geometry = "icosahedron",
}: {
  position: [number, number, number]
  rotation?: [number, number, number]
  scale: number
  color: string
  speed?: number
  distort?: number
  geometry?: "icosahedron" | "octahedron" | "dodecahedron" | "torus"
}) {
  const meshRef = useRef<THREE.Mesh>(null)
  const frameCount = useRef(0)

  // Skip frames to reduce CPU/GPU load (run at ~30fps instead of 60fps)
  useFrame((state) => {
    frameCount.current++
    if (frameCount.current % 2 !== 0) return // Skip every other frame

    if (meshRef.current) {
      // Subtle rotation (doubled to compensate for frame skipping)
      meshRef.current.rotation.x += 0.002 * speed
      meshRef.current.rotation.y += 0.004 * speed

      // Subtle floating motion based on time
      meshRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * speed * 0.5) * 0.1
    }
  })

  const GeometryComponent = useMemo(() => {
    switch (geometry) {
      case "octahedron":
        return <octahedronGeometry args={[1, 0]} />
      case "dodecahedron":
        return <dodecahedronGeometry args={[1, 0]} />
      case "torus":
        return <torusGeometry args={[1, 0.4, 16, 32]} />
      default:
        return <icosahedronGeometry args={[1, 1]} />
    }
  }, [geometry])

  return (
    <Float speed={speed} rotationIntensity={0.5} floatIntensity={0.5}>
      <mesh ref={meshRef} position={position} rotation={rotation || [0, 0, 0]} scale={scale}>
        {GeometryComponent}
        <MeshDistortMaterial
          color={color}
          transparent
          opacity={0.15}
          distort={distort}
          speed={2}
          roughness={0.4}
          metalness={0.8}
        />
      </mesh>
    </Float>
  )
}

// Mouse-following effect for the scene
function MouseFollower() {
  const { mouse, viewport } = useThree()
  const groupRef = useRef<THREE.Group>(null)

  useFrame(() => {
    if (groupRef.current) {
      // Subtle camera/group movement based on mouse position
      groupRef.current.rotation.y = THREE.MathUtils.lerp(
        groupRef.current.rotation.y,
        mouse.x * 0.1,
        0.05
      )
      groupRef.current.rotation.x = THREE.MathUtils.lerp(
        groupRef.current.rotation.x,
        -mouse.y * 0.1,
        0.05
      )
    }
  })

  return <group ref={groupRef} />
}

// Main scene with multiple floating shapes and scroll parallax
function Scene({ scrollProgress }: { scrollProgress: number }) {
  const groupRef = useRef<THREE.Group>(null)
  const { mouse } = useThree()
  const frameCount = useRef(0)

  // Skip frames to reduce CPU/GPU load
  useFrame(() => {
    frameCount.current++
    if (frameCount.current % 2 !== 0) return // Skip every other frame

    if (groupRef.current) {
      // Parallax effect based on mouse
      groupRef.current.rotation.y = THREE.MathUtils.lerp(
        groupRef.current.rotation.y,
        mouse.x * 0.15,
        0.06 // Doubled to compensate for frame skipping
      )
      groupRef.current.rotation.x = THREE.MathUtils.lerp(
        groupRef.current.rotation.x,
        -mouse.y * 0.1,
        0.06
      )

      // Scroll-based parallax - shapes move at different speeds
      groupRef.current.position.y = THREE.MathUtils.lerp(
        groupRef.current.position.y,
        scrollProgress * 2,
        0.2
      )
    }
  })

  // Calculate opacity based on scroll (fade out as user scrolls)
  const opacity = Math.max(0.15 - scrollProgress * 0.1, 0.05)

  return (
    <group ref={groupRef}>
      {/* Primary burgundy shape - prominent (moves faster with scroll) */}
      <FloatingShape
        position={[-3, 1 - scrollProgress * 3, -2]}
        scale={1.0}
        color="#8B2635"
        speed={0.8}
        distort={0.3}
        geometry="icosahedron"
      />

      {/* Gold accent shape (moves slower - depth effect) */}
      <FloatingShape
        position={[3.5, -0.5 - scrollProgress * 1.5, -3]}
        scale={0.8}
        color="#C9A227"
        speed={1.2}
        distort={0.25}
        geometry="octahedron"
      />

      {/* Secondary burgundy shape */}
      <FloatingShape
        position={[0, -2 - scrollProgress * 2, -4]}
        scale={0.6}
        color="#A63D4D"
        speed={1}
        distort={0.3}
        geometry="dodecahedron"
      />

      {/* Small gold torus (fastest parallax) */}
      <FloatingShape
        position={[4, 2 - scrollProgress * 4, -5]}
        scale={0.4}
        color="#D4B84A"
        speed={1.5}
        distort={0.2}
        geometry="torus"
      />

      {/* Small burgundy shape */}
      <FloatingShape
        position={[-4, -1.5 - scrollProgress * 2.5, -4]}
        scale={0.5}
        color="#8B2635"
        speed={0.9}
        distort={0.2}
        geometry="icosahedron"
      />
    </group>
  )
}

// Scene wrapper to pass scroll progress
function SceneWrapper() {
  const scrollProgress = useScrollProgress()
  return <Scene scrollProgress={scrollProgress} />
}

// Exported canvas component
export function FloatingShapesCanvas({ className }: { className?: string }) {
  return (
    <div className={className}>
      <Canvas
        camera={{ position: [0, 0, 6], fov: 45 }}
        dpr={[1, 1.5]} // Reduced from [1, 2] for better performance
        gl={{
          antialias: false, // Disable for performance (shapes are blurred anyway)
          alpha: true,
          powerPreference: "default", // Let browser decide optimal power mode
        }}
        performance={{ min: 0.5 }} // Allow adaptive performance scaling
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <pointLight position={[-10, -10, -5]} intensity={0.5} color="#C9A227" />
        <SceneWrapper />
      </Canvas>
    </div>
  )
}
